import { type ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableColumnHeader } from "@/components/data-tables/data-table-column-header";
import { formatDate, cn } from "@/lib/utils";
import { UserActions } from "./UserActions";
import { z } from "zod";
import { findUser } from "@shared/validators/user-validator";
import i18n from "@/utils/i18n/i18n";

type User = z.infer<typeof findUser>;

interface UserManagementActions {
    onViewDetails?: (user: User) => void;
    onEdit?: (user: User) => void;
    onToggleEnabled?: (user: User) => void;
    onDuplicate?: (user: User) => void;
    onDelete?: (user: User) => void;
    onResendInvite?: (user: User) => void;
    onCopyInviteLink?: (user: User) => void;
}

export function createUserManagementColumns(actions: UserManagementActions): ColumnDef<User>[] {
    return [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label={i18n.t("columns.selectAll")}
                    className="translate-y-[2px]"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label={i18n.t("columns.selectRow")}
                    className="translate-y-[2px]"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "name",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.name")} />,
            cell: ({ row }) => {
                return (
                    <div className="flex space-x-2">
                        <span className="max-w-32 truncate font-medium sm:max-w-72 md:max-w-[31rem]">
                            {row.getValue("name")}
                        </span>
                    </div>
                );
            },
        },
        {
            accessorKey: "email",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.email")} />,
            cell: ({ row }) => {
                return (
                    <div className="flex space-x-2">
                        <span className="max-w-32 truncate sm:max-w-72 md:max-w-[31rem]">
                            {row.getValue("email")}
                        </span>
                    </div>
                );
            },
        },
        {
            accessorKey: "department",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.department")} />,
            cell: ({ row }) => {
                return (
                    <div className="flex space-x-2">
                        <span className="max-w-32 truncate sm:max-w-72 md:max-w-[31rem]">
                            {row.getValue("department") || "-"}
                        </span>
                    </div>
                );
            },
        },
        {
            accessorKey: "role",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.role")} />,
            cell: ({ row }) => {
                const role = row.getValue("role") as string;
                return (
                    <div className="flex space-x-2">
                        <span className="max-w-32 truncate sm:max-w-72 md:max-w-[31rem]">
                            {role?.charAt(0).toUpperCase() + role?.slice(1) || i18n.t("columns.user")}
                        </span>
                    </div>
                );
            },
            filterFn: (row, id, value) => {
                return value.includes(row.getValue(id));
            },
        },
        {
            accessorKey: "authSource",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.source")} />,
            cell: ({ row }) => {
                const authSource = row.getValue("authSource") as string;
                const getSourceLabel = (source: string) => {
                    switch (source?.toLowerCase()) {
                        case "invited":
                            return i18n.t("columns.invited");
                        case "google":
                            return i18n.t("columns.google");
                        case "microsoft":
                            return i18n.t("columns.entraId");
                        default:
                            return i18n.t("columns.local");
                    }
                };
                const isInvited = authSource?.toLowerCase() === "invited";
                return (
                    <span className={cn("text-sm", isInvited && "text-amber-600 dark:text-amber-400")}>
                        {getSourceLabel(authSource || 'local')}
                    </span>
                );
            },
            filterFn: (row, id, value) => {
                return value.includes(row.getValue(id));
            },
        },
        {
            accessorKey: "twoFactorEnabled",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.twoFa")} />,
            cell: ({ row }) => {
                const twoFactorEnabled = row.getValue("twoFactorEnabled") as boolean;
                return (
                    <div className="flex space-x-2">
                        <span className="max-w-32 truncate sm:max-w-72 md:max-w-[31rem]">
                            {twoFactorEnabled ? i18n.t("columns.enabled") : i18n.t("columns.disabled")}
                        </span>
                    </div>
                );
            },
        },
        {
            accessorKey: "createdAt",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.created")} />,
            cell: ({ row }) => formatDate(row.getValue("createdAt")),
        },
        {
            id: "actions",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.actions")} className="text-end" />,
            cell: ({ row }) => (
                <div className="flex justify-end">
                    <UserActions
                        user={row.original}
                        onEdit={actions.onEdit}
                        onViewDetails={actions.onViewDetails}
                        onToggleEnabled={actions.onToggleEnabled}
                        onDuplicate={actions.onDuplicate}
                        onDelete={actions.onDelete}
                        onResendInvite={actions.onResendInvite}
                        onCopyInviteLink={actions.onCopyInviteLink}
                    />
                </div>
            ),
        },
    ];
}

export const UserManagementSearchableColumns = [
    {
        id: "name" as keyof User,
        get title() { return i18n.t("columns.name"); },
    },
    {
        id: "email" as keyof User,
        get title() { return i18n.t("columns.email"); },
    },
    {
        id: "department" as keyof User,
        get title() { return i18n.t("columns.department"); },
    },
];

export const UserManagementFilterableColumns = [
    {
        id: "role" as keyof User,
        get title() { return i18n.t("columns.role"); },
        get options() {
            return [
                { label: i18n.t("columns.admin"), value: "admin" },
                { label: i18n.t("columns.user"), value: "user" },
            ];
        },
    },
    {
        id: "authSource" as keyof User,
        get title() { return i18n.t("columns.source"); },
        get options() {
            return [
                { label: i18n.t("columns.invited"), value: "invited" },
                { label: i18n.t("columns.local"), value: "local" },
                { label: i18n.t("columns.google"), value: "google" },
                { label: i18n.t("columns.microsoft"), value: "microsoft" },
            ];
        },
    },
    {
        id: "twoFactorEnabled" as keyof User,
        get title() { return i18n.t("columns.twoFa"); },
        get options() {
            return [
                { label: i18n.t("columns.enabled"), value: true },
                { label: i18n.t("columns.disabled"), value: false },
            ];
        },
    },
];